import { useState } from 'react';
import { AxiosError } from 'axios';
import {
  Alert,
  Button,
  ChevronRightIcon,
  Chip,
  Collapse,
  DeleteForeverIcon,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  ExpandMoreIcon,
  FolderIcon,
  HistoryIcon,
  InsertDriveFileIcon,
  Spinner,
  Text,
} from '@billda/design-system';
import {
  useRecycleBin,
  useRestoreFile,
  useRestoreFolder,
  usePermanentDeleteFile,
  usePermanentDeleteFolder,
} from '../../hooks/useRecycleBin';
import { RestoreCollisionDialog } from './RestoreCollisionDialog';
import type { RecycleBinItemDto } from '../../api/recycleBinApi';
import { formatFileSize } from './formatFileSize';

interface RecycleBinListProps {
  siteId?: number;
}

interface TreeNode {
  item: RecycleBinItemDto;
  children: TreeNode[];
}

function buildTree(items: RecycleBinItemDto[]): TreeNode[] {
  const itemMap = new Map<string, TreeNode>();
  const roots: TreeNode[] = [];

  for (const item of items) {
    itemMap.set(item.id, { item, children: [] });
  }

  for (const item of items) {
    const node = itemMap.get(item.id)!;
    const parentNode = item.originalParentId
      ? itemMap.get(item.originalParentId)
      : undefined;

    if (parentNode) {
      parentNode.children.push(node);
    } else {
      roots.push(node);
    }
  }

  // Sort children: folders first, then by name
  const sortChildren = (nodes: TreeNode[]) => {
    nodes.sort((a, b) => {
      if (a.item.type !== b.item.type) {
        return a.item.type === 'folder' ? -1 : 1;
      }
      return a.item.name.localeCompare(b.item.name);
    });
    for (const node of nodes) {
      sortChildren(node.children);
    }
  };
  sortChildren(roots);

  return roots;
}

function getCountdownDays(scheduledPurgeAt: string | null): number {
  if (!scheduledPurgeAt) {
    return 0;
  }
  const purgeDate = new Date(scheduledPurgeAt);
  const now = new Date();
  const diffMs = purgeDate.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
}

function getCountdownColor(days: number): 'default' | 'warning' | 'error' {
  if (days <= 1) {
    return 'error';
  }
  if (days <= 3) {
    return 'warning';
  }
  return 'default';
}

function getCountdownLabel(days: number): string {
  if (days === 0) {
    return 'Expires today';
  }
  if (days === 1) {
    return '1 day left';
  }
  return `${days} days left`;
}

function RecycleBinTreeNode({
  node,
  depth,
  expanded,
  onToggle,
  onRestore,
  onDelete,
}: {
  node: TreeNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onRestore: (item: RecycleBinItemDto) => void;
  onDelete: (item: RecycleBinItemDto) => void;
}) {
  const { item } = node;
  const isFolder = item.type === 'folder';
  const hasChildren = node.children.length > 0;
  const isExpanded = expanded.has(item.id);
  const days = getCountdownDays(item.scheduledPurgeAt);
  const chipColor = getCountdownColor(days);
  const chipLabel = getCountdownLabel(days);
  const isTopLevel = depth === 0;
  const isToggleable = isFolder && hasChildren;

  return (
    <>
      <div
        role={isToggleable ? 'button' : undefined}
        tabIndex={isToggleable ? 0 : undefined}
        onClick={() => {
          if (isToggleable) {
            onToggle(item.id);
          }
        }}
        onKeyDown={event => {
          if (isToggleable && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            onToggle(item.id);
          }
        }}
        className="flex items-center gap-2 border-b border-border-default py-2 pr-4 hover:bg-surface-raised sm:pr-6"
        style={{ paddingLeft: `${(depth + 1) * 3}rem` }}
        data-testid={`recycle-row-${item.id}`}
      >
        {isToggleable ? (
          isExpanded ? (
            <ExpandMoreIcon
              className="size-4.5 text-content-secondary"
              aria-hidden="true"
            />
          ) : (
            <ChevronRightIcon
              className="size-4.5 text-content-secondary"
              aria-hidden="true"
            />
          )
        ) : (
          <div className="w-4.5" />
        )}

        {isFolder ? (
          <FolderIcon
            className="size-5 text-content-secondary"
            aria-hidden="true"
          />
        ) : (
          <InsertDriveFileIcon
            className="size-5 text-content-secondary"
            aria-hidden="true"
          />
        )}

        <div className="min-w-0 flex-1">
          <Text variant="body2" noWrap>
            {item.name}
          </Text>
          {isTopLevel && item.originalFolderPath && (
            <Text
              variant="caption"
              color="secondary"
              className="block truncate"
            >
              {item.originalFolderPath}
            </Text>
          )}
          <Text variant="caption" color="secondary" className="block">
            {!isFolder && item.fileSizeBytes != null
              ? formatFileSize(item.fileSizeBytes)
              : ''}
            {isTopLevel && item.originalSiteName
              ? ` · ${item.originalSiteName}`
              : ''}
            {isTopLevel && item.deletedBy
              ? ` · Deleted by ${item.deletedBy}`
              : ''}
          </Text>
        </div>

        {isTopLevel && (
          <Chip
            label={chipLabel}
            size="sm"
            color={chipColor}
            variant="outlined"
          />
        )}

        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={e => {
            e.stopPropagation();
            onRestore(item);
          }}
          title="Restore"
          aria-label="Restore"
          data-testid={`restore-button-${item.id}`}
        >
          <HistoryIcon className="size-4" aria-hidden="true" />
        </Button>

        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={e => {
            e.stopPropagation();
            onDelete(item);
          }}
          title="Delete forever"
          aria-label="Delete forever"
          data-testid={`permanent-delete-button-${item.id}`}
        >
          <DeleteForeverIcon className="size-4" aria-hidden="true" />
        </Button>
      </div>

      {isFolder && hasChildren && (
        <Collapse in={isExpanded} unmountOnExit>
          {node.children.map(child => (
            <RecycleBinTreeNode
              key={child.item.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              onRestore={onRestore}
              onDelete={onDelete}
            />
          ))}
        </Collapse>
      )}
    </>
  );
}

export function RecycleBinList({ siteId }: RecycleBinListProps) {
  const { data: items, isLoading } = useRecycleBin(siteId);
  const restoreFile = useRestoreFile(siteId);
  const restoreFolder = useRestoreFolder(siteId);
  const permanentDeleteFile = usePermanentDeleteFile(siteId);
  const permanentDeleteFolder = usePermanentDeleteFolder(siteId);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<RecycleBinItemDto | null>(
    null
  );
  const [collisionInfo, setCollisionInfo] = useState<{
    fileId: string;
    fileName: string;
    conflictInfo: {
      conflictingFileId: string;
      conflictingFileName: string;
      targetFolderId: string | null;
    };
  } | null>(null);

  const handleToggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleRestore = async (item: RecycleBinItemDto) => {
    if (item.type === 'folder') {
      restoreFolder.mutate(item.id);
      return;
    }

    try {
      await restoreFile.mutateAsync({ fileId: item.id });
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.status === 409) {
        const data = error.response.data as
          | {
              conflictingFileId?: string;
              conflictingFileName?: string;
              targetFolderId?: string | null;
            }
          | undefined;

        if (data?.conflictingFileId && data.conflictingFileName) {
          setCollisionInfo({
            fileId: item.id,
            fileName: item.name,
            conflictInfo: {
              conflictingFileId: data.conflictingFileId,
              conflictingFileName: data.conflictingFileName,
              targetFolderId: data.targetFolderId ?? null,
            },
          });
        }
      }
    }
  };

  const handlePermanentDelete = (item: RecycleBinItemDto) => {
    if (item.type === 'folder') {
      permanentDeleteFolder.mutate(item.id);
    } else {
      permanentDeleteFile.mutate(item.id);
    }
    setDeleteConfirm(null);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size={32} />
      </div>
    );
  }

  const tree = items ? buildTree(items) : [];

  return (
    <div data-testid="recycle-bin-list">
      <Alert severity="info" className="mx-4 mt-4 mb-2 sm:mx-6">
        Files in the recycle bin are permanently deleted after 7 days.
      </Alert>

      {tree.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-12">
          <DeleteForeverIcon
            className="size-12 text-content-disabled"
            aria-hidden="true"
          />
          <Text variant="body2" color="secondary">
            Recycle bin is empty
          </Text>
        </div>
      ) : (
        <div>
          {tree.map(node => (
            <RecycleBinTreeNode
              key={node.item.id}
              node={node}
              depth={0}
              expanded={expanded}
              onToggle={handleToggle}
              onRestore={handleRestore}
              onDelete={setDeleteConfirm}
            />
          ))}
        </div>
      )}

      {/* Permanent delete confirmation */}
      <Dialog
        open={deleteConfirm !== null}
        onOpenChange={open => !open && setDeleteConfirm(null)}
      >
        <DialogContent>
          <DialogHeader>Permanently delete?</DialogHeader>
          <DialogBody>
            Permanently delete &quot;{deleteConfirm?.name}&quot;? This action
            cannot be undone.
          </DialogBody>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setDeleteConfirm(null)}
              data-testid="permanent-delete-cancel-button"
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() =>
                deleteConfirm && handlePermanentDelete(deleteConfirm)
              }
              data-testid="permanent-delete-confirm-button"
            >
              Delete forever
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore collision dialog */}
      {collisionInfo && (
        <RestoreCollisionDialog
          open
          onClose={() => setCollisionInfo(null)}
          conflictInfo={collisionInfo.conflictInfo}
          fileId={collisionInfo.fileId}
          fileName={collisionInfo.fileName}
          siteId={siteId}
        />
      )}
    </div>
  );
}
